import { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import "./AdminDepartmentsRoles.css";

const cx = (...c) => c.filter(Boolean).join(" ");

const DEFAULT_STRUCTURE = [
  {
    name: "HR Department",
    description: "Human Resources",
    roles: [
      {
        name: "HR Manager",
        description: "HR leadership",
        permissions: [
          "employees:view",
          "employees:edit",
          "attendance:view",
          "attendance:approve",
          "leave:view",
          "leave:approve",
          "salary:view",
          "performance:view",
          "hr:onboarding:manage",
          "hr:offboarding:manage",
          "hr:recruitment:manage",
          "hr:policies:manage",
          "hr:training:manage",
          "hr:grievance:manage",
          "hr:vacancy:manage",
          "hr:interview:manage",
          "hr:offer-letter:manage",
          "hr:holiday:manage",
          "hr:attendance-rule:manage",
          "hr:salary-structure:manage",
          "hr:payslip:manage",
          "hr:resignation:manage",
          "hr:exit-interview:manage",
          "hr:letters:manage",
          "hr:documents:view",
          "hr:payroll:manage",
          "hr:deduction:manage",
          "hr:candidate:manage",
          "hr:appraisal:manage",
          "reports:hr:view",
          "reports:view",
        ],
      },
      {
        name: "HR Executive",
        description: "HR operations",
        permissions: [
          "employees:view",
          "employees:add",
          "employees:edit",
          "attendance:view",
          "attendance:submit",
          "leave:view",
          "employees:documents:upload",
          "hr:onboarding:manage",
          "hr:recruitment:manage",
          "hr:policies:manage",
          "hr:training:manage",
          "hr:vacancy:manage",
          "hr:interview:manage",
          "hr:offer-letter:manage",
          "hr:holiday:manage",
          "hr:attendance-rule:manage",
          "hr:salary-structure:manage",
          "hr:payslip:manage",
          "hr:resignation:manage",
          "hr:exit-interview:manage",
          "hr:letters:manage",
          "hr:documents:view",
          "hr:payroll:manage",
          "hr:deduction:manage",
          "hr:candidate:manage",
          "hr:appraisal:manage",
        ],
      },
    ],
  },
  {
    name: "Accounts Department",
    description: "Finance & Accounts",
    roles: [
      {
        name: "Accounts Manager",
        description: "Finance approvals",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "finance:invoice:create",
          "finance:invoice:approve",
          "finance:expense:create",
          "finance:expense:approve",
          "finance:vendor-bill:upload",
          "finance:vendor-bill:approve",
          "finance:payments:approve",
          "finance:payments:update",
          "finance:payments:release",
          "finance:gst:view",
          "finance:tds:view",
          "finance:reports:view",
          "finance:audit-logs:view",
          "salary:process",
          "salary:approve",
          "salary:view",
        ],
      },
      {
        name: "Accounts Executive",
        description: "Finance entries",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "finance:invoice:create",
          "finance:expense:create",
          "finance:vendor-bill:upload",
          "finance:payments:update",
          "finance:gst:view",
          "finance:tds:view",
          "salary:view",
        ],
      },
      {
        name: "Accounts Auditor",
        description: "Audit & reports (read-only)",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "finance:reports:view",
          "finance:audit-logs:view",
          "finance:gst:view",
          "finance:tds:view",
          "salary:view",
        ],
      },
      {
        name: "Payroll Officer",
        description: "Payroll processing",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "salary:view",
          "salary:process",
          "salary:approve",
        ],
      },
    ],
  },
  {
    name: "Sales Department",
    description: "Sales & Business",
    roles: [
      {
        name: "Sales Head",
        description: "Sales leadership",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "leads:view",
          "leads:edit",
          "leads:assign",
          "deals:view",
          "deals:edit",
          "reports:create",
          "discount:approve",
          "pricing:approve",
          "sales:targets:manage",
          "sales:team:performance:view",
          "reports:sales:view",
        ],
      },
      {
        name: "Sales Manager",
        description: "Sales management",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "leads:view",
          "leads:edit",
          "leads:assign",
          "deals:view",
          "deals:edit",
          "reports:create",
          "sales:team:performance:view",
          "reports:sales:view",
        ],
      },
      {
        name: "Sales Executive",
        description: "Sales operations",
        permissions: [
          "attendance:view",
          "attendance:submit",
          "idcard:view",
          "leads:view",
          "deals:view",
          "deals:edit",
          "deals:close:request",
          "reports:create",
        ],
      },
    ],
  },
  {
    name: "Guard / Security Department",
    description: "Security & Gate",
    roles: [
      {
        name: "Security Guard",
        description: "Security operations",
        permissions: [
          "security:vendor-entry:create",
          "security:material-inward:create",
          "security:vehicle-log:create",
          "security:challan:upload",
          "purchase-order:view",
        ],
      },
    ],
  },
  {
    name: "Plant Manager Department",
    description: "Plant operations",
    roles: [
      {
        name: "Plant Manager",
        description: "Plant leadership",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "production:plan",
          "production:report",
          "stock:consumption:view",
          "maintenance:log",
          "quality:view",
          "wastage:view",
          "plant:shift:manage",
          "plant:overtime:approve",
          "plant:utilization:view",
          "plant:breakdown:view",
          "plant:maintenance:approve",
          "plant:quality:review",
          "plant:rework:approve",
          "plant:inventory:alert",
          "plant:material-movement:approve",
          "plant:contractor-work:approve",
          "plant:safety:manage",
          "plant:compliance:manage",
          "plant:coordination:manage",
          "plant:reports:manage",
        ],
      },
    ],
  },
  {
    name: "Plant Purchase Department",
    description: "Plant procurement",
    roles: [
      {
        name: "Plant Purchase Head",
        description: "Plant purchasing",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "vendor-master:edit",
          "purchase:request:create",
          "purchase:order:create",
          "purchase:rate-comparison:view",
          "material:availability:view",
          "vendors:view",
        ],
      },
    ],
  },
  {
    name: "Store & Inventory Department",
    description: "Stores and inventory control",
    roles: [
      {
        name: "Store Keeper",
        description: "Daily store operations",
        permissions: [
          "inventory:stock:view",
          "inventory:stock:update",
          "inventory:grn:create",
          "inventory:issue:return",
        ],
      },
      {
        name: "Inventory Manager",
        description: "Inventory oversight",
        permissions: [
          "inventory:stock:view",
          "inventory:stock:update",
          "inventory:grn:create",
          "inventory:issue:return",
          "inventory:reports:view",
        ],
      },
    ],
  },
  {
    name: "Logistics & Dispatch Department",
    description: "Dispatch and logistics",
    roles: [
      {
        name: "Dispatch Manager",
        description: "Dispatch oversight",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "logistics:shipment:view",
          "logistics:shipment:create",
          "logistics:dispatch:update",
          "logistics:tracking:view",
          "logistics:gatepass:create",
          "logistics:planning:manage",
          "logistics:packing:manage",
          "logistics:transport:manage",
          "logistics:dispatch:manage",
          "logistics:tracking:manage",
          "logistics:returns:manage",
          "logistics:reports:manage",
        ],
      },
      {
        name: "Logistics Coordinator",
        description: "Shipment coordination",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "logistics:shipment:view",
          "logistics:dispatch:update",
          "logistics:tracking:view",
          "logistics:planning:manage",
          "logistics:packing:manage",
          "logistics:transport:manage",
          "logistics:dispatch:manage",
          "logistics:tracking:manage",
          "logistics:returns:manage",
          "logistics:reports:manage",
        ],
      },
    ],
  },
  {
    name: "Quality Assurance Department",
    description: "Quality checks and compliance",
    roles: [
      {
        name: "QA Inspector",
        description: "Quality inspection",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "reports:create",
          "reports:view",
          "quality:check:create",
          "quality:check:view",
          "quality:reject:create",
        ],
      },
      {
        name: "QA Manager",
        description: "Quality management",
        permissions: [
          "attendance:submit",
          "attendance:view",
          "idcard:view",
          "reports:create",
          "reports:view",
          "quality:check:create",
          "quality:check:view",
          "quality:reject:create",
          "quality:approve",
          "quality:reports:view",
        ],
      },
    ],
  },
];

function Button({ variant = "outline", className = "", children, ...props }) {
  return (
    <button
      className={cx("adr-btn", variant, className)}
      {...props}
    >
      {children}
    </button>
  );
}

function Badge({ children, className = "" }) {
  return <span className={cx("adr-chip", className)}>{children}</span>;
}

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="adr-modal-backdrop">
      <div className="adr-modal">
        <div className="adr-modal-header">
          <div className="adr-modal-title">{title}</div>
          <button className="adr-btn outline" onClick={onClose}>Close</button>
        </div>
        <div className="adr-modal-body">{children}</div>
        {footer ? <div className="adr-modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export default function AdminDepartmentsRoles() {
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [deptForm, setDeptForm] = useState({ name: "", description: "" });
  const [editDept, setEditDept] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    departmentId: "",
    permissions: [],
    description: "",
  });

  const [editRole, setEditRole] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState([]);
  const [permQ, setPermQ] = useState("");
  const [q, setQ] = useState("");

  const [assignForm, setAssignForm] = useState({
    employeeId: "",
    departmentId: "",
    roleId: "",
  });

  const loadAll = async () => {
    setLoading(true);
    setMsg("");
    try {
      const [deptRes, roleRes, permRes, empRes] = await Promise.all([
        API.get("/admin/departments"),
        API.get("/admin/roles"),
        API.get("/admin/permissions"),
        API.get("/admin/employees"),
      ]);
      setDepartments(deptRes.data || []);
      setRoles(roleRes.data || []);
      setPermissions(permRes.data?.permissions || []);
      setEmployees(empRes.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredRoles = useMemo(() => {
    const qq = (q || "").toLowerCase();
    const list = roles.filter((r) => {
      if (!qq) return true;
      const p = (r.permissions || []).join(", ").toLowerCase();
      return (r.name || "").toLowerCase().includes(qq) || p.includes(qq);
    });
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [roles, q]);

  const permsResults = useMemo(() => {
    if (!permQ) return permissions;
    const qq = permQ.toLowerCase();
    return permissions.filter((p) => String(p).toLowerCase().includes(qq));
  }, [permissions, permQ]);

  const assignRolesForDept = useMemo(() => {
    if (!assignForm.departmentId) return roles;
    return roles.filter(
      (r) => String(r.department?._id || r.department) === String(assignForm.departmentId)
    );
  }, [roles, assignForm.departmentId]);

  const togglePermission = (perm) => {
    setPickerValue((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const createDepartment = async (e) => {
    e.preventDefault();
    if (!deptForm.name) return;
    await API.post("/admin/departments", deptForm);
    setDeptForm({ name: "", description: "" });
    loadAll();
  };

  const createRole = async (e) => {
    e.preventDefault();
    if (!roleForm.name || !roleForm.departmentId) return;
    await API.post("/admin/roles", {
      ...roleForm,
      permissions: pickerValue,
    });
    setRoleForm({ name: "", departmentId: "", permissions: [], description: "" });
    setPickerValue([]);
    loadAll();
  };

  const startEdit = (r) => {
    setEditRole({
      id: r._id,
      name: r.name || "",
      departmentId: r.department?._id || r.department || "",
      description: r.description || "",
    });
    setPickerValue(r.permissions || []);
    setPickerOpen(true);
  };

  const startEditDept = (d) => {
    setEditDept({
      id: d._id,
      name: d.name || "",
      description: d.description || "",
    });
  };

  const saveEditDept = async () => {
    if (!editDept?.id) return;
    await API.put(`/admin/departments/${editDept.id}`, {
      name: editDept.name,
      description: editDept.description,
    });
    setEditDept(null);
    loadAll();
  };

  const removeDept = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    await API.delete(`/admin/departments/${d._id}`);
    loadAll();
  };

  const seedDefaults = async () => {
    try {
      setMsg("Creating default departments and roles...");
      const deptByName = new Map(
        (departments || []).map((d) => [String(d.name).toLowerCase(), d])
      );

      const createdDepartments = [];
      for (const dept of DEFAULT_STRUCTURE) {
        const key = String(dept.name).toLowerCase();
        if (!deptByName.has(key)) {
          const res = await API.post("/admin/departments", {
            name: dept.name,
            description: dept.description || "",
          });
          deptByName.set(key, res.data);
          createdDepartments.push(res.data);
        }
      }

      const roleKey = (deptId, roleName) =>
        `${String(deptId)}::${String(roleName).toLowerCase()}`;
      const roleMap = new Map(
        (roles || []).map((r) => [roleKey(r.department?._id || r.department, r.name), r])
      );

      for (const dept of DEFAULT_STRUCTURE) {
        const d = deptByName.get(String(dept.name).toLowerCase());
        if (!d?._id) continue;
        for (const role of dept.roles || []) {
          const key = roleKey(d._id, role.name);
          if (roleMap.has(key)) {
            const existing = roleMap.get(key);
            const currentPerms = Array.isArray(existing.permissions) ? existing.permissions : [];
            const desired = role.permissions || [];
            const merged = Array.from(new Set([...currentPerms, ...desired]));
            await API.put(`/admin/roles/${existing._id}`, {
              name: existing.name,
              departmentId: d._id,
              permissions: merged,
              description: role.description || existing.description || "",
            });
          } else {
            const created = await API.post("/admin/roles", {
              name: role.name,
              departmentId: d._id,
              permissions: role.permissions || [],
              description: role.description || "",
            });
            roleMap.set(key, created.data);
          }
        }
      }

      setMsg("Default departments and roles created/updated.");
      loadAll();
    } catch (e) {
      setMsg(e?.response?.data?.message || "Failed to create default roles");
    }
  };

  const forceSyncAll = async () => {
    try {
      setMsg("Syncing all roles permissions...");
      const deptByName = new Map(
        (departments || []).map((d) => [String(d.name || "").toLowerCase(), d])
      );

      const roleMap = new Map(
        (roles || []).map((r) => [
          `${String(r.department?._id || r.department)}::${String(r.name || "").toLowerCase()}`,
          r,
        ])
      );

      for (const dept of DEFAULT_STRUCTURE) {
        const d = deptByName.get(String(dept.name).toLowerCase());
        if (!d?._id) continue;
        for (const role of dept.roles || []) {
          const key = `${String(d._id)}::${String(role.name).toLowerCase()}`;
          const existing = roleMap.get(key);
          if (!existing) continue;
          await API.put(`/admin/roles/${existing._id}`, {
            name: existing.name,
            departmentId: d._id,
            permissions: role.permissions || [],
            description: role.description || existing.description || "",
          });
        }
      }

      setMsg("All roles synced.");
      loadAll();
    } catch (e) {
      setMsg(e?.response?.data?.message || "Failed to sync roles");
    }
  };

  const saveEdit = async () => {
    if (!editRole?.id) return;
    await API.put(`/admin/roles/${editRole.id}`, {
      name: editRole.name,
      departmentId: editRole.departmentId,
      description: editRole.description,
      permissions: pickerValue,
    });
    setEditRole(null);
    setPickerValue([]);
    setPickerOpen(false);
    loadAll();
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    await API.delete(`/admin/roles/${r._id}`);
    loadAll();
  };

  const assignRole = async (e) => {
    e.preventDefault();
    if (!assignForm.employeeId || !assignForm.departmentId || !assignForm.roleId) return;
    await API.put(`/admin/employees/${assignForm.employeeId}/assign-role`, {
      departmentId: assignForm.departmentId,
      roleId: assignForm.roleId,
    });
    setAssignForm({ employeeId: "", departmentId: "", roleId: "" });
    loadAll();
  };

  const unassignRole = async (employeeId) => {
    await API.put(`/admin/employees/${employeeId}/unassign-role`);
    loadAll();
  };

  return (
    <div className="adr-page">
      <div className="adr-container">
        <div className="adr-header">
          <div className="adr-title">Departments & Roles</div>
          <div className="adr-subtitle">
            Create departments and roles. Permissions define what each role can view or edit.
          </div>
          <div className="adr-header-actions">
            <Button variant="brand" onClick={seedDefaults}>
              Load Default Roles & Permissions
            </Button>
            <Button variant="outline" onClick={forceSyncAll}>
              Sync Roles with Defaults
            </Button>
          </div>
        </div>

        {msg ? <div className="adr-note">{msg}</div> : null}
        {loading ? <div className="adr-subtitle">Loading…</div> : null}

        <div className="adr-grid two">
          <div className="adr-card">
            <div className="adr-card-header">
              <div className="adr-card-title">Departments</div>
            </div>
            <div className="adr-card-body">
              <form className="adr-row two" onSubmit={createDepartment}>
                <div>
                  <div className="adr-label">Department name</div>
                  <input
                    className="adr-input"
                    value={deptForm.name}
                    onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                    placeholder="e.g. Operations"
                  />
                </div>
                <div>
                  <div className="adr-label">Description</div>
                  <input
                    className="adr-input"
                    value={deptForm.description}
                    onChange={(e) =>
                      setDeptForm({ ...deptForm, description: e.target.value })
                    }
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Button variant="brand" type="submit">Add Department</Button>
                </div>
              </form>

              <div className="adr-table-wrap" style={{ marginTop: 12 }}>
                <table className="adr-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((d) => (
                      <tr key={d._id}>
                        <td>{d.name}</td>
                        <td>{d.description || "-"}</td>
                        <td>
                          <div className="adr-actions">
                            <Button variant="brand" onClick={() => startEditDept(d)}>
                              Edit
                            </Button>
                            <Button variant="danger" onClick={() => removeDept(d)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {departments.length === 0 && (
                      <tr>
                        <td colSpan={3} className="adr-empty">
                          No departments yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="adr-dept-cards">
                {departments.length === 0 ? (
                  <div className="adr-empty">No departments yet</div>
                ) : (
                  departments.map((d) => (
                    <div key={d._id} className="adr-card-item">
                      <div className="adr-card-title">{d.name}</div>
                      <div className="adr-card-sub">{d.description || "-"}</div>
                      <div className="adr-card-actions">
                        <Button variant="brand" onClick={() => startEditDept(d)}>
                          Edit
                        </Button>
                        <Button variant="danger" onClick={() => removeDept(d)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="adr-card">
            <div className="adr-card-header">
              <div className="adr-card-title">Create Role</div>
            </div>
            <div className="adr-card-body">
              <form className="adr-row" onSubmit={createRole}>
                <div className="adr-row two">
                  <div>
                    <div className="adr-label">Role name</div>
                    <input
                      className="adr-input"
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      placeholder="e.g. Field Executive"
                    />
                  </div>
                  <div>
                    <div className="adr-label">Department</div>
                    <select
                      className="adr-select"
                      value={roleForm.departmentId}
                      onChange={(e) =>
                        setRoleForm({ ...roleForm, departmentId: e.target.value })
                      }
                    >
                      <option value="">Select department</option>
                      {departments.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <div className="adr-label">Description</div>
                  <input
                    className="adr-input"
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, description: e.target.value })
                    }
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <div className="adr-label">Permissions</div>
                  <div className="adr-subtitle">
                    Choose what this role can access (screens, actions, approvals).
                  </div>
                  <div className="adr-pill-list">
                    {pickerValue.length === 0 ? (
                      <span className="adr-subtitle">No permissions selected</span>
                    ) : (
                      pickerValue.map((p) => (
                        <Badge key={p}>
                          {p}
                          <button type="button" onClick={() => togglePermission(p)}>
                            x
                          </button>
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                <div className="adr-row two">
                  <Button type="button" onClick={() => setPickerOpen(true)}>
                    Choose Permissions
                  </Button>
                  <Button variant="brand" type="submit">
                    Add Role
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="adr-card">
          <div className="adr-card-header">
            <div className="adr-card-title">Roles</div>
            <div className="adr-toolbar">
              <div className="adr-search">
                <span>S</span>
                <input
                  className="adr-input"
                  placeholder="Search by role or permission"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="adr-card-body">
            <table className="adr-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Permissions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((r) => (
                  <tr key={r._id}>
                    <td>{r.name}</td>
                    <td>{r.department?.name || "-"}</td>
                    <td>{(r.permissions || []).join(", ") || "-"}</td>
                    <td>
                      <div className="adr-actions">
                        <Button variant="brand" onClick={() => startEdit(r)}>
                          Edit
                        </Button>
                        <Button variant="danger" onClick={() => removeRole(r)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRoles.length === 0 && (
                  <tr>
                    <td colSpan={4} className="adr-empty">
                      No roles yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="adr-role-cards">
              {filteredRoles.length === 0 ? (
                <div className="adr-empty">No roles yet</div>
              ) : (
                filteredRoles.map((r) => (
                  <div key={r._id} className="adr-card-item">
                    <div className="adr-card-title">{r.name}</div>
                    <div className="adr-card-sub">{r.department?.name || "-"}</div>
                    <div className="adr-card-sub">{(r.permissions || []).join(", ") || "-"}</div>
                    <div className="adr-card-actions">
                      <Button variant="brand" onClick={() => startEdit(r)}>
                        Edit
                      </Button>
                      <Button variant="danger" onClick={() => removeRole(r)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="adr-card">
          <div className="adr-card-header">
          <div className="adr-card-title">Assign Employees</div>
        </div>
        <div className="adr-card-body">
            <form className="adr-row three" onSubmit={assignRole}>
              <div>
                <div className="adr-label">Employee</div>
                <select
                  className="adr-select"
                  value={assignForm.employeeId}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, employeeId: e.target.value })
                  }
                >
                  <option value="">Select employee</option>
                  {employees.map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.fullName} ({e.mobile})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="adr-label">Department</div>
                <select
                  className="adr-select"
                  value={assignForm.departmentId}
                  onChange={(e) =>
                    setAssignForm({
                      ...assignForm,
                      departmentId: e.target.value,
                      roleId: "",
                    })
                  }
                >
                  <option value="">Select department</option>
                  {departments.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="adr-label">Role</div>
                <select
                  className="adr-select"
                  value={assignForm.roleId}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, roleId: e.target.value })
                  }
                >
                  <option value="">Select role</option>
                  {assignRolesForDept.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Button variant="brand" type="submit">Assign Role</Button>
              </div>
            </form>

            <div style={{ marginTop: 16 }}>
              <table className="adr-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp._id}>
                      <td>{emp.fullName}</td>
                      <td>{emp.department?.name || "-"}</td>
                      <td>{emp.employeeRole?.name || "-"}</td>
                      <td>{(emp.employeeRole?.permissions || []).join(", ") || "-"}</td>
                      <td>
                        <div className="adr-actions">
                          <Button
                            variant="danger"
                            onClick={() => unassignRole(emp._id)}
                            disabled={!emp.employeeRole}
                          >
                            Unassign
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr>
                      <td colSpan={5} className="adr-empty">
                        No employees found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="adr-emp-cards">
                {employees.length === 0 ? (
                  <div className="adr-empty">No employees found</div>
                ) : (
                  employees.map((emp) => (
                    <div key={emp._id} className="adr-card-item">
                      <div className="adr-card-title">{emp.fullName}</div>
                      <div className="adr-card-sub">{emp.department?.name || "-"}</div>
                      <div className="adr-card-sub">{emp.employeeRole?.name || "-"}</div>
                      <div className="adr-card-sub">
                        {(emp.employeeRole?.permissions || []).join(", ") || "-"}
                      </div>
                      <div className="adr-card-actions">
                        <Button
                          variant="danger"
                          onClick={() => unassignRole(emp._id)}
                          disabled={!emp.employeeRole}
                        >
                          Unassign
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <Modal
          title={editRole ? "Edit Role & Permissions" : "Choose Permissions"}
          onClose={() => {
            setPickerOpen(false);
            if (!editRole) setPickerValue([]);
          }}
          footer={
            editRole ? (
              <>
                <Button onClick={() => { setPickerOpen(false); setEditRole(null); }}>Cancel</Button>
                <Button variant="brand" onClick={saveEdit}>Save</Button>
              </>
            ) : (
              <>
                <Button onClick={() => setPickerValue([])}>Clear</Button>
                <Button variant="brand" onClick={() => setPickerOpen(false)}>Done</Button>
              </>
            )
          }
        >
          {editRole && (
            <div className="adr-row" style={{ marginBottom: 16 }}>
              <div className="adr-row two">
                <div>
                  <div className="adr-label">Role name</div>
                  <input
                    className="adr-input"
                    value={editRole.name}
                    onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
                  />
                </div>
                <div>
                  <div className="adr-label">Department</div>
                  <select
                    className="adr-select"
                    value={editRole.departmentId}
                    onChange={(e) =>
                      setEditRole({ ...editRole, departmentId: e.target.value })
                    }
                  >
                    <option value="">Select department</option>
                    {departments.map((d) => (
                      <option key={d._id} value={d._id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <div className="adr-label">Description</div>
                <input
                  className="adr-input"
                  value={editRole.description}
                  onChange={(e) =>
                    setEditRole({ ...editRole, description: e.target.value })
                  }
                />
              </div>
            </div>
          )}
          <div className="adr-perm-grid">
            <div>
              <input
                className="adr-input"
                placeholder="Search permissions (e.g. vendors, approvals, reports)"
                value={permQ}
                onChange={(e) => setPermQ(e.target.value)}
              />
              <div style={{ marginTop: 12 }}>
                <div className="adr-perm-group">
                  <div className="adr-label" style={{ marginBottom: 8 }}>All Permissions</div>
                  <div>
                    {permissions.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={cx("adr-perm-pill", pickerValue.includes(p) && "active")}
                        onClick={() => togglePermission(p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="adr-perm-box">
              <div className="adr-label">Search Results</div>
              <div style={{ marginTop: 8 }}>
                {permsResults.length === 0 ? (
                  <div className="adr-subtitle">No matches</div>
                ) : (
                  permsResults.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={cx("adr-perm-pill", pickerValue.includes(p) && "active")}
                      onClick={() => togglePermission(p)}
                    >
                      {p}
                    </button>
                  ))
                )}
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="adr-label">Selected</div>
                <div className="adr-pill-list" style={{ marginTop: 6 }}>
                  {pickerValue.length === 0 ? (
                    <span className="adr-subtitle">Nothing selected</span>
                  ) : (
                    pickerValue.map((p) => (
                      <Badge key={p}>
                        {p}
                        <button type="button" onClick={() => togglePermission(p)}>
                          x
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {editDept && (
        <Modal
          title="Edit Department"
          onClose={() => setEditDept(null)}
          footer={
            <>
              <Button onClick={() => setEditDept(null)}>Cancel</Button>
              <Button variant="brand" onClick={saveEditDept}>Save</Button>
            </>
          }
        >
          <div className="adr-row two">
            <div>
              <div className="adr-label">Department name</div>
              <input
                className="adr-input"
                value={editDept.name}
                onChange={(e) => setEditDept({ ...editDept, name: e.target.value })}
              />
            </div>
            <div>
              <div className="adr-label">Description</div>
              <input
                className="adr-input"
                value={editDept.description}
                onChange={(e) =>
                  setEditDept({ ...editDept, description: e.target.value })
                }
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
